import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

function calcSummary(rows) {
  const sum = (type) => rows.filter(c => c.type === type).reduce((s, c) => s + Number(c.amount), 0);
  return {
    total:     rows.reduce((s, c) => s + Number(c.amount), 0),
    tithes:    sum('Tithe'),
    offerings: sum('Offering'),
    special:   sum('Special'),
    harvest:   sum('Harvest'),
    building:  sum('Building Fund'),
    thanksgiving: sum('Thanksgiving'),
  };
}

export function useContributions(filters = {}) {
  const [contributions, setContributions] = useState([]);
  const [summary,       setSummary]       = useState({});
  const [loading,       setLoading]       = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('contributions')
      .select('*, members(id,first_name,last_name,district_id,districts(name))')
      .order('date', { ascending: false });

    if (filters.member_id) q = q.eq('member_id', filters.member_id);
    if (filters.type)      q = q.eq('type', filters.type);
    if (filters.year)      q = q.gte('date', `${filters.year}-01-01`).lte('date', `${filters.year}-12-31`);
    if (filters.month && filters.year) {
      const m = String(filters.month).padStart(2, '0');
      q = q.gte('date', `${filters.year}-${m}-01`).lte('date', `${filters.year}-${m}-31`);
    }

    const { data, error } = await q;
    if (error) toast.error('Failed to load contributions');
    const rows = data || [];
    setContributions(rows);
    setSummary(calcSummary(rows));
    setLoading(false);
  }, [JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { contributions, summary, loading, refetch: fetch };
}

export async function recordContribution(payload, recordedBy) {
  const { data, error } = await supabase.from('contributions')
    .insert({ ...payload, recorded_by: recordedBy }).select().single();
  if (error) { toast.error(error.message); return null; }
  toast.success('Contribution recorded ✅');
  return data;
}

export async function deleteContribution(id) {
  const { error } = await supabase.from('contributions').delete().eq('id', id);
  if (error) { toast.error(error.message); return false; }
  toast.success('Entry deleted');
  return true;
}

// Monthly breakdown for charts — returns 12-item array
export async function getMonthlyBreakdown(year) {
  const { data } = await supabase.from('contributions').select('date,amount,type')
    .gte('date', `${year}-01-01`).lte('date', `${year}-12-31`);

  return Array.from({ length: 12 }, (_, i) => {
    const m    = String(i + 1).padStart(2, '0');
    const rows = (data || []).filter(c => c.date.startsWith(`${year}-${m}`));
    return {
      month:     new Date(year, i, 1).toLocaleString('default', { month: 'short' }),
      total:     rows.reduce((s, c) => s + Number(c.amount), 0),
      tithes:    rows.filter(c => c.type === 'Tithe').reduce((s, c) => s + Number(c.amount), 0),
      offerings: rows.filter(c => c.type === 'Offering').reduce((s, c) => s + Number(c.amount), 0),
    };
  });
}

// District breakdown
export async function getDistrictBreakdown(year) {
  const { data } = await supabase.from('contributions')
    .select('amount,members(districts(name))')
    .gte('date', `${year}-01-01`).lte('date', `${year}-12-31`);

  const out = {};
  (data || []).forEach(c => {
    const d = c.members?.districts?.name || 'Unknown';
    out[d] = (out[d] || 0) + Number(c.amount);
  });
  return out;
}
